import { DateResult, InkDateType } from '../../types';
import { getValidLocale } from '../internal/getValidLocale';
import { extractStandardDateTimeParts } from '../internal/standardDateTime/extractStandardDateTimeParts';
import { getSupportedFormatsForLocale } from '../internal/getSupportedDateFormatsForLocale';
import { isValidDate } from '../internal/temporalDate/dateBuilder';
import { extractDateComponents } from '../../parsers/dateInput/getDateParts';

type NormalizeDateArgs = {
  value: InkDateType;
  locale?: string;
  isStrict?: boolean;
};
/**
 * Converts a date input (`Date`, `Moment.js`, ISO 8601, or formatted string) into structured components.
 *
 * Handles common date formats automatically:
 * - JavaScript `Date` objects (`new Date()`)
 * - `Moment.js` objects
 * - ISO 8601 date strings (`"YYYY-MM-DD"`, `"YYYY-MM-DDTHH:mm:ss.sssZ"`)
 *
 * Parses and validates non-standard formats when needed:
 * - If the input format is not immediately recognized, `normalizeDate` calls `dateParser`
 *   to extract correct date components before conversion happens.
 * - Prevents invalid dates like `"2024-02-30"` from rolling over to `"2024-03-01"`.
 *
 * Supports locale-based date formats (via `dateParser`):
 * - `"15/06/2024"` is correctly interpreted as `DD/MM/YYYY` for `en-GB`.
 * - `"06/15/2024"` is correctly interpreted as `MM/DD/YYYY` for `en-US`.
 *
 * The final output is either a valid structured date or `undefined` if the input is invalid.
 *
 * @param {InkDateType} value - The date input (`Date`, `Moment.js`, or a formatted string).
 * @param {LocaleType} locale - The locale used for parsing user-entered formats.
 * @returns {DateParts | undefined} Extracted structured components, or `undefined` if the input is not valid.
 */
export const normalizeDate = ({ value, locale, isStrict = true }: NormalizeDateArgs): DateResult => {
  const result = extractStandardDateTimeParts(value);
  if (result) {
    const [dateTimeParts, timeZoneSource] = result;
    return {
      dateParts: {
        day: dateTimeParts.day,
        month: dateTimeParts.month,
        year: dateTimeParts.year,
      },
      isValidDate: true,
      timeZoneSource,
      input: value.toString(),
    };
  }

  const supportedFormats = getSupportedFormatsForLocale(getValidLocale(locale));

  const validLocale = getValidLocale(locale);
  const dateParts = extractDateComponents({
    value: value?.toString(),
    supportedFormats,
    locale: validLocale,
    isStrictMode: isStrict,
  });
  return {
    dateParts: dateParts ?? { day: -1, month: -1, year: -1 },
    isValidDate: !!(dateParts && isValidDate(dateParts)),
    input: value?.toString(),
    timeZoneSource: 'local',
  };
};
